import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../providers/TranslationProvider';
import { SCREENS } from '../../utils/constants';
import { Eye, EyeOff, ChevronRight, Lock, Mail, ArrowLeft, AlertCircle } from 'lucide-react';
import Logo from '../../icons/logo.svg';

const LoginScreen = () => {
    const { navigateToScreen, isDarkMode } = useUI();
    const { login, loading, error: authError } = useAuth();
    const { t } = useTranslation();

    // States
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setLocalError('');

        if (!email || !password) {
            setLocalError(t('login.error.empty', 'Please fill in all fields'));
            return;
        }

        try {
            await login(email, password);
            navigateToScreen(SCREENS.DASHBOARD);
        } catch (err) {
            console.error(err);
            setLocalError(t('login.error.failed', 'Login failed. Check your credentials.'));
        }
    };

    // Animation Variants
    const containerVariants = {
        initial: { opacity: 0, x: 20 },
        animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
        exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
    };

    // Styles (Tiimo-like: Rounded, Glass)
    const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
    const inputBg = isDarkMode
        ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:bg-slate-800/80'
        : 'bg-white border-blue-100 text-slate-800 placeholder-slate-400 focus:bg-white';

    // Softer, more organic feel
    const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

    return (
        <motion.div
            className={`w-full min-h-screen flex flex-col items-center justify-center ${textClass}`}
            variants={containerVariants}
            initial="initial"
            animate="animate"
            exit="exit"
        >
            {/* Back Button */}
            <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => navigateToScreen(SCREENS.WELCOME)}
                className="absolute top-6 left-6 z-20 p-2.5 rounded-full hover:bg-white/10 transition-colors backdrop-blur-sm" // Slightly larger tap target
                aria-label="Back"
            >
                <ArrowLeft size={24} className={isDarkMode ? 'text-white' : 'text-slate-800'} />
            </motion.button>

            {/* Main Content Card */}
            <motion.div
                className={`relative z-10 w-full max-w-md p-8 md:p-10 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg}`}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Header Section */}
                <div className="flex flex-col items-center mb-8">
                    <div className={`w-20 h-20 mb-6 rounded-3xl flex items-center justify-center ${isDarkMode ? 'bg-slate-800 shadow-lg shadow-blue-900/20' : 'bg-white shadow-xl shadow-blue-100'}`}>
                        <img src={Logo} alt="IDApps" className="w-12 h-12 object-contain" />
                    </div>
                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600">
                        {t('login.welcomeBack', 'Welcome Back')}
                    </h2>
                    <p className={`text-sm mt-2 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {t('login.subtitle', 'Sign in to access your digital vault')}
                    </p>
                </div>

                <form onSubmit={handleLogin} className="space-y-6">
                    {/* Email Input */}
                    <div className="space-y-2">
                        <label className={`text-xs ml-3 font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {t('login.email', 'Username or Email')}
                        </label>
                        <div className="relative group">
                            <input
                                type="text"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={`w-full px-5 py-4 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all border ${inputBg}`}
                                placeholder="Enter your username"
                            />
                            <Mail size={18} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 opacity-70 group-focus-within:text-blue-500 group-focus-within:opacity-100 transition-all" />
                        </div>
                    </div>

                    {/* Password Input */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-center ml-3 mr-1">
                            <label className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {t('login.password', 'Password')}
                            </label>
                            <button
                                type="button"
                                onClick={() => navigateToScreen(SCREENS.FORGOT_PASSWORD)}
                                className="text-xs font-medium text-blue-500 hover:text-blue-400 transition-colors"
                            >
                                {t('login.forgot', 'Forgot Password?')}
                            </button>
                        </div>
                        <div className="relative group">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={`w-full px-5 py-4 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all border ${inputBg}`}
                                placeholder="••••••••"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500 transition-colors p-2"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Error Message */}
                    <AnimatePresence>
                        {(localError || authError) && (
                            <motion.div
                                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                className="flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm overflow-hidden"
                            >
                                <AlertCircle size={18} className="shrink-0" />
                                <span className="font-medium">{localError || authError?.message || 'Authentication failed'}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Submit Button */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={loading}
                        type="submit"
                        className={`w-full py-4 rounded-full font-bold text-lg shadow-xl shadow-blue-500/20 relative overflow-hidden group mb-2 ${isDarkMode
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30 hover:shadow-blue-900/50'
                            : 'bg-slate-900 text-white shadow-slate-200 hover:bg-slate-800'
                            }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center gap-2">
                                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                {t('login.processing', 'Processing...')}
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                {t('login.submit', 'Sign In')}
                                <ChevronRight size={20} />
                            </span>
                        )}
                    </motion.button>
                </form>

                {/* Footer Link */}
                <div className="mt-8 text-center">
                    <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {t('login.noAccount', "Don't have an ID? ")}
                        <button
                            onClick={() => navigateToScreen(SCREENS.SIGNUP)}
                            className="font-bold text-blue-500 hover:text-blue-400 transition-colors ml-1"
                        >
                            {t('login.create', 'Create Identity')}
                        </button>
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default LoginScreen;
